import type { ScanBarState } from '../shell/scanFocus'

/**
 * The product's signature surface. Docked bottom on every station screen, per
 * ui-design-system.md section 7 and the thumb-zone rule in 10.2: the camera looks
 * up at the book, the field and the last-scan line sit low where the thumb is.
 *
 * It carries the attention halo whenever the next action is a scan, which is
 * almost always. A sheet that has claimed focus takes the halo with it, so the
 * one-pulse rule holds without either component knowing about the other.
 */

export type CameraState = 'off' | 'starting' | 'streaming' | 'denied' | 'unavailable'

export interface ScanBarProps {
  state: ScanBarState
  camera?: CameraState
  /** The live preview element, supplied by the camera hook. Absent in tests and on desktop. */
  videoRef?: React.Ref<HTMLVideoElement>
  inputRef?: React.Ref<HTMLInputElement>
  onSubmit?: () => void
  onBlur?: () => void
  onCapturePhoto?: () => void
}

export function ScanBar({ state, camera = 'off', videoRef, inputRef, onSubmit, onBlur, onCapturePhoto }: ScanBarProps) {
  const owns = state.mounted && !state.suspendedBy
  const last = state.lastScan
  // accept-pop and reject-shake are the named animations; the class is the whole
  // contract, the keyframes live in styles.css behind prefers-reduced-motion.
  const verdictClass = last && last.message ? (last.result === 'accept' ? 'accept-pop' : 'reject-shake') : ''

  return (
    <div className={`scanbar${owns ? ' pulse-halo' : ''}`} data-context={state.context}>
      {camera === 'streaming' || camera === 'starting' ? (
        <div className="scanbar-camera">
          <video ref={videoRef} className="scanbar-video" muted playsInline autoPlay />
          <button type="button" className="scanbar-capture" onClick={onCapturePhoto}>
            Photo
          </button>
        </div>
      ) : null}

      {camera === 'denied' || camera === 'unavailable' ? (
        // No dead UI on a refusal (10.1): the preview is simply absent and the bar
        // says which path is still open, rather than leaving a black rectangle.
        <p className="scanbar-note muted">
          {camera === 'denied' ? 'Camera blocked - use the scanner or type the code' : 'No camera - use the scanner or type the code'}
        </p>
      ) : null}

      <div className="scanbar-row">
        <span className="chip chip-context">{state.context}</span>
        <span className="scanbar-next">{state.nextAction}</span>
      </div>

      <div className={`scanbar-last ${verdictClass}`} aria-live="polite">
        {last && last.message ? (
          <>
            <span className="scanbar-code">{last.code}</span>
            <span className="scanbar-message">{last.message}</span>
          </>
        ) : (
          <span className="scanbar-message muted">Ready</span>
        )}
      </div>

      <form
        className="scanbar-form"
        onSubmit={(e) => {
          e.preventDefault()
          onSubmit?.()
        }}
      >
        <input
          ref={inputRef}
          className="scanbar-input"
          inputMode="none"
          autoComplete="off"
          spellCheck={false}
          aria-label={state.nextAction}
          onBlur={onBlur}
        />
      </form>
      {state.inFlight ? <div className="scanbar-progress" /> : null}
    </div>
  )
}
